/**
 * 全カード型を 1 つの React Flow ノードコンポーネントで処理。
 * type ごとにアイコン・色・サブタイトル整形を切替。
 * 仕様書: docs/01_spec/spec_line_bot.md A-11.5
 */
import type { CampaignElement, CampaignElementType } from '@goenhub/shared';
import type { NodeProps } from '@xyflow/react';
import {
  AlertCircle,
  BookOpen,
  Calendar,
  CalendarClock,
  Calculator,
  ClipboardList,
  Clock,
  GitBranch,
  LayoutTemplate,
  Link as LinkIcon,
  Megaphone,
  MessagesSquare,
  PenSquare,
  Send,
  StickyNote,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useParams } from 'react-router-dom';


import { BaseCardNode } from './BaseCardNode';

import { useLiffSettings } from '@/hooks/useSettings';
import { useTargetReach } from '@/hooks/useCampaignReach';

import type { AudienceEventType } from '@goenhub/shared';

/** カード type → audience_events.type へのマッピング（reach 集計用） */
const REACH_TYPE_BY_CARD: Partial<Record<string, AudienceEventType>> = {
  mail_send: 'mail_sent',
  mail_schedule: 'mail_sent',
  line_news: 'line_news_received',
  form: 'form_submitted',
  landing: 'landing_view',
  document_request: 'document_view',
  briefing_request: 'form_submitted',
  quote_request: 'form_submitted',
};


interface NodeStyle {
  icon: LucideIcon;
  accent: string;
  title: string;
  /** refId 未設定時に表示する役割説明（Phase 23a-ext-3） */
  roleHint: string;
}

const NODE_STYLES: Record<CampaignElementType, NodeStyle> = {
  line_news: {
    icon: Megaphone,
    accent: 'border-[#06c755]/60',
    title: 'LINE ニュース',
    roleHint: 'LINE 公式から一斉配信',
  },
  mail_schedule: {
    icon: CalendarClock,
    accent: 'border-[var(--accent)]/60',
    title: '配信予約への追加',
    roleHint: '既存予約に audience を追加（締切型）',
  },
  mail_send: {
    icon: Send,
    accent: 'border-[var(--accent)]/80',
    title: 'メール配信',
    roleHint: '個別タイミングで動的に送信（ナーチャリング型）',
  },
  mail_draft: {
    icon: PenSquare,
    accent: 'border-[var(--accent)]/40',
    title: 'メール下書き',
    roleHint: '（旧仕様：メール配信に統合）',
  },
  form: {
    icon: ClipboardList,
    accent: 'border-[var(--accent-purple)]/60',
    title: 'フォーム',
    roleHint: 'URL 共有で回答を集める',
  },
  landing: {
    icon: LayoutTemplate,
    accent: 'border-[var(--accent-purple)]/80',
    title: 'ランディング',
    roleHint: '公開ランディングページに誘導',
  },
  referral: {
    icon: LinkIcon,
    accent: 'border-[var(--accent-amber)]/80',
    title: 'referral',
    roleHint: 'SNS 広告 / QR / 紙媒体等のタグ付き URL',
  },
  mail_contacts_group: {
    icon: Users,
    accent: 'border-[var(--accent-cyan)]/60',
    title: '配信先グループ',
    roleHint: 'グループ起点で配信開始',
  },
  event: {
    icon: Calendar,
    accent: 'border-[var(--accent-amber)]/60',
    title: 'イベント',
    roleHint: '指定日時にフローが進む',
  },
  delay: {
    icon: Clock,
    accent: 'border-[var(--text-muted)]/60',
    title: '遅延',
    roleHint: '（旧仕様：線の after_delay に統合）',
  },
  condition: {
    icon: GitBranch,
    accent: 'border-[var(--accent-amber)]/80',
    title: '分岐条件',
    roleHint: '（旧仕様：線の通過条件に統合）',
  },
  metric: {
    icon: AlertCircle,
    accent: 'border-[var(--accent-cyan)]/40',
    title: '計測ポイント',
    roleHint: '（旧仕様：ダッシュボードで代替）',
  },
  document_request: {
    icon: BookOpen,
    accent: 'border-[var(--accent-green)]/60',
    title: '資料請求',
    roleHint: '公開資料へ campaignId 付き CTA リンクを発行',
  },
  briefing_request: {
    icon: MessagesSquare,
    accent: 'border-[var(--accent-purple)]/60',
    title: '個別説明申込',
    roleHint: '個別説明用フォームへ campaignId 付き CTA',
  },
  quote_request: {
    icon: Calculator,
    accent: 'border-[var(--accent-amber)]/60',
    title: '見積依頼',
    roleHint: '見積依頼フォームへ campaignId 付き CTA',
  },
  note: {
    icon: StickyNote,
    accent: 'border-[var(--text-muted)]/40',
    title: 'メモ',
    roleHint: '他のスタッフへの説明書き',
  },
};

function formatDelayMs(ms?: number): string {
  if (!ms || ms <= 0) return '未設定';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec} 秒`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} 分`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 時間`;
  const day = Math.floor(hr / 24);
  return `${day} 日`;
}

export interface CampaignNodeData extends Record<string, unknown> {
  element: CampaignElement;
  onOpen?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export function CampaignNode(props: NodeProps) {
  const data = props.data as CampaignNodeData;
  const { element, onOpen, onDelete } = data;
  const style = NODE_STYLES[element.type];
  const Icon = style.icon;

  // form カード専用: LIFF URL を動的生成（Phase 24f）。
  // settings/liff から liffId を取得 + URL から campaignId を抽出。
  // 値の変更は再ビルド不要で全フォームカードに即反映される。
  const { id: campaignId } = useParams<{ id: string }>();
  const { data: liffSettings } = useLiffSettings();
  const liffUrl =
    element.type === 'form' && element.refId && liffSettings?.liffId && campaignId
      ? `https://liff.line.me/${liffSettings.liffId}?formId=${encodeURIComponent(element.refId)}&campaignId=${encodeURIComponent(campaignId)}`
      : undefined;

  // referral カード専用：発行 URL を生成（コピー用）。
  // externalUrl は base URL（タグなし）が入っているので、ここで src=referral & ref を付与する。
  const referralUrl = (() => {
    if (element.type !== 'referral') return undefined;
    if (!element.externalUrl || !element.referralRefTag) return undefined;
    const params: Array<[string, string]> = [];
    if (campaignId) params.push(['campaignId', campaignId]);
    params.push(['src', 'referral']);
    params.push(['ref', element.referralRefTag]);
    const sep = element.externalUrl.includes('?') ? '&' : '?';
    const qs = params
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    return `${element.externalUrl}${sep}${qs}`;
  })();

  // plan adaptive-crown §機能 6: 各カードの到達者数を集計
  const reachType = REACH_TYPE_BY_CARD[element.type] ?? null;
  const { data: reachCount } = useTargetReach(
    campaignId ?? null,
    reachType,
    element.refId ?? null,
  );
  const reachLabel = (() => {
    switch (element.type) {
      case 'mail_send':
      case 'mail_schedule':
        return '送信';
      case 'line_news':
        return 'LINE';
      case 'form':
        return '送信';
      case 'landing':
        return '閲覧';
      case 'document_request':
        return '閲覧';
      case 'briefing_request':
      case 'quote_request':
        return '送信';
      default:
        return undefined;
    }
  })();

  const subtitle = (() => {
    if (element.type === 'note') return element.note ?? style.roleHint;
    if (element.type === 'delay')
      return `${formatDelayMs(element.delayMs)} 待機`;
    if (element.type === 'condition') {
      const def = element.conditionDef;
      if (!def) return style.roleHint;
      switch (def.kind) {
        case 'always':
          return '全員';
        case 'has_tag':
          return `タグ: ${def.tag ?? ''}`;
        case 'in_group':
          return `グループ: ${def.groupId ?? ''}`;
        case 'form_answered':
          return `フォーム回答: ${def.formId ?? ''}`;
        case 'not_form_answered':
          return `未回答: ${def.formId ?? ''}`;
        default:
          return style.roleHint;
      }
    }
    return element.refLabel ?? element.refId ?? style.roleHint;
  })();

  return (
    <BaseCardNode
      type={element.type}
      accentClass={style.accent}
      icon={<Icon className="size-3.5 flex-shrink-0" />}
      title={style.title}
      subtitle={subtitle}
      externalUrl={element.type === 'referral' ? undefined : element.externalUrl}
      liffUrl={liffUrl}
      referralUrl={referralUrl}
      onOpen={onOpen ? () => onOpen(element.id) : undefined}
      onDelete={onDelete ? () => onDelete(element.id) : undefined}
      enableHandles={element.type !== 'note'}
      reachCount={reachCount ?? null}
      reachLabel={reachLabel}
    />
  );
}
